import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api-error';

/**
 * Configuración de TanStack Query para toda la app.
 *
 * Decisiones:
 * - `staleTime` de 30s: los paneles se navegan mucho entre sí; evita refetches al volver.
 * - `retry`: nunca reintenta 4xx (401/403/404/400 no se arreglan reintentando; además un
 *   401 ya dispara la limpieza de sesión). Los 5xx y los fallos de red sí (2-3 intentos con
 *   backoff exponencial).
 * - `refetchOnWindowFocus` desactivado: el backend recibe webhooks, no queremos ráfagas de
 *   queries cada vez que el usuario cambia de pestaña.
 * - Las mutaciones no se reintentan solas (podrían duplicar una orden).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            if (error.isNetworkError) return failureCount < 2;
            if (error.status >= 400 && error.status < 500) return false;
            return failureCount < 3;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/** Instancia única usada por la app (se limpia al cerrar sesión / recibir 401). */
export const queryClient = createQueryClient();
